// Parallax Service - Simple database operations

import logger from '../utils/logger.js';
import apiClient from './apiClient';

const parallaxService = {
  // Event listeners for scene updates
  _sceneUpdateListeners: [],
  
  // Add listener for scene updates
  addSceneUpdateListener(callback) {
    this._sceneUpdateListeners.push(callback);
  },
  
  // Remove listener for scene updates
  removeSceneUpdateListener(callback) {
    this._sceneUpdateListeners = this._sceneUpdateListeners.filter(cb => cb !== callback);
  },
  
  // Notify all listeners that scenes have been updated
  _notifySceneUpdate() {
    this._sceneUpdateListeners.forEach((callback, index) => {
      try {
        callback();
      } catch (error) {
        console.error('Error in scene update listener:', error);
      }
    });
  },
  // Save a scene to the database (creates new or updates existing)
  async saveScene(sceneData) {
    try {
      logger.log('💾 saveScene called with data:', sceneData);
      
      try {
        const result = await apiClient.requestJson({
          path: `/parallax-scenes/${encodeURIComponent(sceneData.name)}`,
          method: 'PUT',
          body: sceneData
        });
        logger.log('✅ Scene saved successfully, notifying listeners...');
        
        // Notify all listeners that a scene has been updated
        this._notifySceneUpdate();
        
        return result;
      } catch (putError) {
        try {
          const created = await apiClient.requestJson({
            path: '/parallax-scenes',
            method: 'POST',
            body: sceneData
          });
          logger.log('✅ Scene created successfully, notifying listeners...');
          this._notifySceneUpdate();
          return created;
        } catch (postError) {
          console.error('❌ Failed to save scene:', postError);
          return null;
        }
      }
    } catch (error) {
      console.error('❌ Error saving scene:', error);
      return null;
    }
  },

  // Load a scene from the database
  async loadScene(sceneName) {
    try {
      const scene = await apiClient.requestJson({
        path: `/parallax-scenes/${encodeURIComponent(sceneName)}`
      });
      return scene?.data || scene || null;
    } catch (error) {
      console.error('❌ Error loading scene:', error);
      return null;
    }
  },

  // Get all scenes
  async getAllScenes() {
    try {
      const scenes = await apiClient.requestJson({ path: '/parallax-scenes' });
      return scenes?.data || scenes || [];
    } catch (error) {
      console.error('❌ Error loading scenes:', error);
      return [];
    }
  },

  // Delete a scene
  async deleteScene(sceneName) {
    try {
      const result = await apiClient.requestJson({
        path: `/parallax-scenes/${encodeURIComponent(sceneName)}`,
        method: 'DELETE'
      });
      return !!result;
    } catch (error) {
      console.error('❌ Error deleting scene:', error);
      return false;
    }
  },

  // Get scenes by category
  async getScenesByCategory(category) {
    try {
      const scenes = await apiClient.requestJson({
        path: '/parallax-scenes',
        params: { category }
      });
      return scenes?.data || scenes || [];
    } catch (error) {
      console.error('❌ Error loading scenes for category:', error);
      return [];
    }
  },

  // (duplicate deleteScene removed - see method above)

  // Clear all scenes
  async clearAllScenes() {
    try {
      // First get all scenes
      const scenes = await this.getAllScenes();
      const scenesArray = Array.isArray(scenes) ? scenes : (scenes?.data || []);
      
      if (scenesArray.length === 0) {
        // No scenes to clear
        return true;
      }
      
      // Delete each scene one by one
      const deletePromises = scenesArray.map(scene => 
        this.deleteScene(scene.name)
      );
      
      await Promise.all(deletePromises);
      // Scenes cleared successfully
      return true;
    } catch (error) {
      console.error('❌ Error clearing scenes:', error);
      return false;
    }
  },

  // Instance-Scene Assignment Management
  // Save instance-scene assignment to database
  async saveInstanceAssignment(instanceId, sceneName, backgroundPath = null) {
    try {
      const assignmentData = {
        instance_id: instanceId,
        scene_name: sceneName,
        assigned_at: new Date().toISOString(),
        background_path: backgroundPath
      };
      
      const result = await apiClient.requestJson({
        path: '/parallax-instance-assignments',
        method: 'POST',
        body: assignmentData
      });
      return result;
    } catch (error) {
      console.error('❌ Error saving instance assignment:', error);
      return null;
    }
  },

  // Get instance-scene assignment from database
  async getInstanceAssignment(instanceId) {
    try {
      try {
        const assignment = await apiClient.requestJson({
          path: `/parallax-instance-assignments/${encodeURIComponent(instanceId)}`
        });
        return assignment;
      } catch (err) {
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting instance assignment:', error);
      return null;
    }
  },

  // Clear instance-scene assignment from database
  async clearInstanceAssignment(instanceId) {
    try {
      try {
        await apiClient.requestJson({
          path: `/parallax-instance-assignments/${encodeURIComponent(instanceId)}`,
          method: 'DELETE'
        });
        return true;
      } catch (_) {
        return true;
      }
    } catch (error) {
      console.error('❌ Error clearing instance assignment:', error);
      return false;
    }
  },

  // Category-Scene Assignment Management
  // Get all categories
  async getAllCategories() {
    try {
      const result = await apiClient.requestJson({ path: '/categories' });
      return result.data || [];
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      return [];
    }
  },

  // Get category scene assignment
  async getCategorySceneAssignment(category) {
    try {
      const assignment = await apiClient.requestJson({
        path: `/category-scene-assignments/${encodeURIComponent(category)}`
      });
      return assignment.data;
    } catch (error) {
      console.error('❌ Error getting category assignment:', error);
      return null;
    }
  },

  // Get all category scene assignments
  async getAllCategorySceneAssignments() {
    try {
      // Use the same base URL logic as databaseService for production vs development
      const result = await apiClient.requestJson({ path: '/category-scene-assignments' });
      return result.data || [];
    } catch (error) {
      console.error('❌ Error loading category assignments:', error);
      return [];
    }
  },

  // Save category scene assignment
  async saveCategorySceneAssignment(category, sceneName) {
    try {
      const result = await apiClient.requestJson({
        path: '/category-scene-assignments',
        method: 'POST',
        body: { category, scene_name: sceneName }
      });
      return result;
    } catch (error) {
      console.error('❌ Error saving category assignment:', error);
      return null;
    }
  },

  // Delete category scene assignment
  async deleteCategorySceneAssignment(category) {
    try {
      try {
        await apiClient.requestJson({
          path: `/category-scene-assignments/${encodeURIComponent(category)}`,
          method: 'DELETE'
        });
        return true;
      } catch (_) {
        return false;
      }
    } catch (error) {
      console.error('❌ Error deleting category assignment:', error);
      return false;
    }
  },

  // Listing-Scene Insertion Management
  // Get all listing scene insertions
  async getAllListingSceneInsertions() {
    try {
      const result = await apiClient.requestJson({ path: '/listing-scene-insertions' });
      return result.data || [];
    } catch (error) {
      console.error('❌ Error loading listing scene insertions:', error);
      return [];
    }
  },

  // Get listing scene insertion for specific listing
  async getListingSceneInsertion(listingId) {
    try {
      try {
        const result = await apiClient.requestJson({
          path: `/listing-scene-insertions/${encodeURIComponent(listingId)}`
        });
        return result.data;
      } catch (_) {
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting listing scene insertion:', error);
      return null;
    }
  },

  // Save listing scene insertion
  async saveListingSceneInsertion(listingId, sceneName) {
    try {
      const result = await apiClient.requestJson({
        path: '/listing-scene-insertions',
        method: 'POST',
        body: { listing_id: listingId, scene_name: sceneName }
      });
      return result;
    } catch (error) {
      console.error('❌ Error saving listing scene insertion:', error);
      return null;
    }
  },

  // Delete listing scene insertion
  async deleteListingSceneInsertion(listingId) {
    try {
      try {
        await apiClient.requestJson({
          path: `/listing-scene-insertions/${encodeURIComponent(listingId)}`,
          method: 'DELETE'
        });
        return true;
      } catch (_) {
        return false;
      }
    } catch (error) {
      console.error('❌ Error deleting listing scene insertion:', error);
      return false;
    }
  }
};

export default parallaxService; 